<template>
  <v-card>
    <v-list dense three-line>
      <v-subheader v-if="!trendingActive">
        {{ results.length }} {{ $translate('PRODUCTS') }}
      </v-subheader>
      <v-subheader v-else>
        {{ $translate('TRENDING_PRODUCTS') }}
      </v-subheader>

      <template v-for="(product, index) in results">
        <v-divider v-if="index > 0" :key="index" inset />
        <v-list-item :key="product.Id" @click="goToProduct(product)">
          <v-list-item-avatar>
            <v-img :src="product.PictureUrl" />
          </v-list-item-avatar>

          <v-list-item-content>
            <!-- Highlighting label -->
            <HighlightingLabel
              :label="product.HighlightingLabel"
              :visible="product.Highlighted"
              style="min-width: 0px"
            />

            <v-list-item-title v-html="product.Name" />
            <v-list-item-subtitle v-html="product.Description" />
          </v-list-item-content>

          <v-list-item-action v-if="showPrice(product)">
            <v-col class="pa-0 text-right">
              <v-list-item-title>
                <span v-if="isOnDiscount(product)" class="discount-price mr-1">
                  {{ formatter.format(getShownRetailUnitPrice(product)) }}
                </span>
                <span
                  :class="
                    hasGeneralDiscount(product) ? 'red--text' : 'text--primary'
                  "
                >
                  {{ formatter.format(getShownUnitPrice(product)) }}
                </span>
                <span v-if="isWeightedProduct(product)" class="text-caption">
                  /
                  <span v-if="product.WeightedInfo.OverviewSize > 1">
                    {{ product.WeightedInfo.OverviewSize }}
                  </span>
                  {{ product.WeightedInfo.Unit }}
                </span>
              </v-list-item-title>

              <div v-if="isProfUser" class="text-caption text--secondary">
                {{ $translate('EXCL_VAT') }}
              </div>

              <div
                v-if="
                  isWeightedProduct(product) &&
                  getDisplayMinQuantity(product) > getDisplayStepSize(product)
                "
                class="text-caption"
              >
                Minimum
                {{ getDisplayMinQuantity(product) }}
                {{ getDisplayUnit(product) }}
              </div>
            </v-col>
          </v-list-item-action>
        </v-list-item>
      </template>
    </v-list>
  </v-card>
</template>

<script>
import utility from '@/shared/utility';
import uiHelpers from '@/shared/ui_helpers';
import {
  hasGeneralDiscount,
  isOnDiscount,
  personalDiscount,
  applyPersonalDiscount,
  isWeightedProduct,
  getUnitPrice,
  getRetailUnitPrice,
  getOverviewPrice,
  getRetailOverviewPrice,
  getDisplayMinQuantity,
  getDisplayUnit,
  getDisplayStepSize,
} from '@/objects/Product';
import HighlightingLabel from '@/components/generalComponents/HighlightingLabel';

export default {
  name: 'SearchResults',
  mixins: [uiHelpers],
  props: ['trendingActive', 'results', 'searchString'],
  components: {
    HighlightingLabel,
  },
  computed: {
    isProfUser() {
      return this.$store.getters.isProfUser;
    },
  },
  data() {
    return {
      formatter: new Intl.NumberFormat('nl', {
        style: 'currency',
        currency: 'EUR',
      }),
    };
  },
  methods: {
    goToProduct(product) {
      this.$emit('goToProduct', product);
    },
    getProductPrice(product) {
      if (applyPersonalDiscount(product)) {
        return this.getProductPriceWithPersonalDiscount(product);
      }
      return this.getProductPriceWithoutPersonalDiscount(product);
    },
    getProductRetailPrice(product) {
      if (applyPersonalDiscount(product)) {
        return this.getProductRetailPriceWithPersonalDiscount(product);
      }
      return this.getProductRetailPriceWithoutPersonalDiscount(product);
    },
    getProductPriceWithPersonalDiscount(product) {
      const price = this.getProductPriceWithoutPersonalDiscount(product);
      return this.getPriceForPersonalDiscount(price, product);
    },
    getProductPriceWithoutPersonalDiscount(product) {
      if (this.isProfUser) {
        return product.PriceExclVat;
      }
      return product.Price;
    },
    getProductRetailPriceWithPersonalDiscount(product) {
      const price = this.getProductPriceWithoutPersonalDiscount(product);
      return this.getProductPriceWithPersonalDiscount(price, product);
    },
    getProductRetailPriceWithoutPersonalDiscount(product) {
      if (this.isProfUser) {
        return utility.getPriceExclVAT(
          product.RetailPrice,
          product.VatPercentage
        );
      }
      return product.RetailPrice;
    },
    getShownRetailUnitPrice(product) {
      if (isWeightedProduct(product)) {
        if (hasGeneralDiscount(product)) {
          return getRetailOverviewPrice(product);
        }
        return getOverviewPrice(product);
      }

      if (hasGeneralDiscount(product)) {
        return getRetailUnitPrice(product);
      }
      return getUnitPrice(product);
    },
    getShownUnitPrice(product) {
      if (isWeightedProduct(product)) {
        return this.getPriceForPersonalDiscount(
          getOverviewPrice(product),
          product
        );
      }
      return this.getPriceForPersonalDiscount(getUnitPrice(product), product);
    },
    getPriceForPersonalDiscount(originalPrice, product) {
      let price = originalPrice;
      if (applyPersonalDiscount(product)) {
        price = utility.getDiscountPrice(
          originalPrice,
          personalDiscount(product)
        );
      }
      return price;
    },
    showPrice(product) {
      if (
        product.Type === 'Rental' &&
        this.getShownUnitPrice(product) < 0.005
      ) {
        return false;
      }
      return true;
    },
    hasGeneralDiscount,
    isOnDiscount,
    isWeightedProduct,
    getDisplayMinQuantity,
    getDisplayUnit,
    getDisplayStepSize,
  },
};
</script>
